import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnInit,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  UntypedFormControl,
  UntypedFormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR, ValidationErrors,
  Validator, Validators
} from "@angular/forms";
import {pairwise, startWith} from "rxjs/operators";
import {Subscription} from "rxjs";
import { NotOnlySpaces } from '../../../shared/CustomValidators/CustomNotOnlySpacesValidator';
import { GoogleBusinessProfileService } from '../../../../services/google-business-profile.service';
import { GooglePlaceDirective } from 'src/app/modules/shared/directives/google-places-autocomplete/google-places-autocomplete.directive';
import { Address } from 'src/app/modules/shared/directives/google-places-autocomplete/google-places-autocomplete.directive';

@Component({
    selector: 'app-company-name',
    templateUrl: './company-name.component.html',
    styleUrls: ['./company-name.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => CompanyNameComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => CompanyNameComponent),
            multi: true
        }
    ],
    standalone: false
})
export class CompanyNameComponent implements OnInit, OnChanges, OnDestroy, ControlValueAccessor, Validator {

  @Input() model: any = {};
  @Input() form: any = {};
  @Input() isEdit = false;
  @Output() callback = new EventEmitter<any>(true);
  @Output() onInitForm = new EventEmitter<any>(true);

  public onModelValueChanged:any = {};
  //@ts-ignore
  public companyFormSubscription: Subscription;

  public companyNameForm: UntypedFormGroup = this.initForm();

  @ViewChild("placesRef") placesRef!: GooglePlaceDirective;

  // Google Places options for business search
  public placeOptions: any = {
    types: ['establishment'],
    componentRestrictions: { country: 'us' },
    fields: ['name', 'formatted_address', 'formatted_phone_number', 'website', 'address_components', 'place_id', 'opening_hours']
  };

  constructor(
    private el: ElementRef,
    private googleBusinessService: GoogleBusinessProfileService
  ) { }


  ngOnChanges(changes: SimpleChanges) {
    this.model = changes?.model?.currentValue;
    this.onModelValueChanged = this.model.model;

  }

  ngOnInit(): void {
    this.initSubscriptions();

    this.companyNameForm = this.initForm(this.onModelValueChanged);
    this.onInitForm.emit(this.companyNameForm);
  }

  preventSpace(event: KeyboardEvent): void {
    const inputElement = event.target as HTMLInputElement;
    if (inputElement.value.trim() === '' && event.key === ' ') {
      event.preventDefault();
    }
  }
  initForm(value: any = ''){
   return  new UntypedFormGroup({
      name: new UntypedFormControl(value? value?.company_name : '',[Validators.required,NotOnlySpaces()]),
    });
  }
  public onTouched: () => void = () => {};

  writeValue(val: any): void {
    val && this.companyNameForm.setValue(val, { emitEvent: false });
  }

  registerOnChange(fn: any): void {
    this.companyNameForm.valueChanges.subscribe(fn);
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    isDisabled ? this.companyNameForm.disable() : this.companyNameForm.enable();
  }

  validate(c: AbstractControl): ValidationErrors | null{
    return this.companyNameForm.valid ? null : { invalidForm: {valid: false, message: "Company name fields are invalid"}};
  }

  initSubscriptions(){
    if (this.form){
      this.companyFormSubscription = this.form.valueChanges
        .pipe(startWith(null), pairwise())
        .subscribe(([prev, next]: [any, any]) => {
          if (this.form.touched){
            this.touchFormControls();
          }
        });
    }

  }
  touchFormControls(){
    Object.keys(this.companyNameForm.controls).forEach(key => {
      this.companyNameForm.get(key)?.markAsTouched();
    });
  }

  // Handle business selection from Google Places autocomplete
  handlePlaceChange(place: Address): void {
    // Set only the business name in the input field (not the full address)
    // Do this immediately to prevent Google Places from setting the formatted address
    this.companyNameForm.get('name')?.setValue(place.name);

    // Extract domain from website
    let domain = '';
    if (place.website) {
      try {
        const urlObj = new URL(place.website);
        domain = urlObj.hostname.replace('www.', '');
      } catch (error) {
        domain = place.website.replace(/^(?:https?:\/\/)?(?:www\.)?/i, '').split('/')[0];
      }
    }

    // Parse address components
    let streetNumber = '';
    let streetName = '';
    let city = '';
    let stateFullName = '';  // Full name like "Wisconsin"
    let zipcode = '';

    if (place.address_components) {
      for (const component of place.address_components) {
        const componentType = component.types[0];

        switch (componentType) {
          case "street_number":
            streetNumber = component.long_name;
            break;
          case "route":
            streetName = component.long_name;
            break;
          case "locality":
            city = component.long_name;
            break;
          case "administrative_area_level_1":
            stateFullName = component.long_name;  // Use FULL name: "Wisconsin" not "WI"
            break;
          case "postal_code":
            zipcode = component.long_name;
            break;
        }
      }
    }

    const addressLine1 = [streetNumber, streetName].filter(Boolean).join(' ');

    // Extract opening hours if available
    let startTime = null;
    let endTime = null;
    if (place.opening_hours && place.opening_hours.periods && place.opening_hours.periods.length > 0) {
      // Get Monday's hours (day 1) or first available day
      const mondayHours = place.opening_hours.periods.find((p: any) => p.open.day === 1) || place.opening_hours.periods[0];
      if (mondayHours && mondayHours.open && mondayHours.close) {
        // Convert 24-hour time to 12-hour format
        startTime = this.convertTo12Hour(mondayHours.open.time);
        endTime = this.convertTo12Hour(mondayHours.close.time);
      }
    }

    // Store business data for company-detail tab
    const businessData = {
      company_name: place.name,
      phone: place.formatted_phone_number,
      website: place.website,
      domain: domain,
      address_line_1: addressLine1,
      city: city,
      state: stateFullName,  // Store full state name: "Wisconsin"
      state_id: stateFullName,
      zipcode: zipcode,
      formatted_address: place.formatted_address,
      start_time: startTime,
      end_time: endTime
    };

    // Store in sessionStorage
    sessionStorage.setItem('selectedBusinessProfile', JSON.stringify(businessData));

    // Emit callback for parent component
    this.callback.emit({
      type: 'businessSelected',
      data: businessData
    });
  }

  // Convert 24-hour time (e.g., "0900") to 12-hour format (e.g., "09:00 AM")
  convertTo12Hour(time24: string): string {
    if (!time24 || time24.length !== 4) return '';

    const hours24 = parseInt(time24.substring(0, 2));
    const minutes = time24.substring(2, 4);

    const period = hours24 >= 12 ? 'PM' : 'AM';
    let hours12 = hours24 % 12;
    if (hours12 === 0) hours12 = 12;

    return `${hours12.toString().padStart(2, '0')}:${minutes} ${period}`;
  }

  ngOnDestroy(){
    // prevent memory leak when component destroyed
    this.companyFormSubscription?.unsubscribe();
  }

}
